import { styled } from 'linaria/react';

// Animation timing curves
// cubic-bezier(0.35, 0.25, 0.31, 1.00) - position changing speed curve
// cubic-bezier(0.33, 0.00, 0.31, 1.00) - rotation speed curve

interface BreathingWrapProps {
  isBreathing: boolean;
  isMoving: boolean;
  isRotating: boolean;
  isReturning: boolean;
  cardWidth: number;
  cardHeight: number;
  breathingPhase?: 'inhale' | 'hold1' | 'exhale' | 'hold2';
  bgColor: string;
}

export const BreathingWrap = styled.div<BreathingWrapProps>`
  position: relative;
  transform-style: preserve-3d;
  background: ${({ bgColor }) => bgColor};
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  will-change: transform;

  /* Always rotate/scale around the center */
  transform-origin: 50% 50%;

  /* Dynamic transition */
  transition: ${({ isBreathing, isMoving, isRotating, isReturning, breathingPhase }) => {
    if (isBreathing) {
      if (breathingPhase === 'inhale') return 'transform 1.65s cubic-bezier(0.35, 0.25, 0.31, 1.00)';
      if (breathingPhase === 'hold1') return 'none';
      if (breathingPhase === 'exhale') return 'transform 2.25s cubic-bezier(0.35, 0.25, 0.31, 1.00)';
      if (breathingPhase === 'hold2') return 'none';
    }
    if (isRotating) return 'transform 1.5s cubic-bezier(0.33, 0.00, 0.31, 1.00)';
    if (isMoving || isReturning) return 'transform 0.5s cubic-bezier(0.35, 0.25, 0.31, 1.00)';
    return 'transform 0.5s cubic-bezier(0.35, 0.25, 0.31, 1.00)';
  }};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    background: ${({ bgColor }) => bgColor};
    opacity: 0;
    transition: opacity 0.3s ease;
    backface-visibility: hidden;
  }

  transform: ${({ isBreathing, isMoving, isRotating, isReturning, cardWidth, cardHeight, breathingPhase }) => {
    const diagonal = Math.sqrt(cardWidth * cardWidth + cardHeight * cardHeight);
    const isPortrait = cardHeight >= cardWidth;
    // Calculate trigger zone height (viewport height minus rootMargin top/bottom)
    const rootMargin = 0.1 * window.innerHeight; // 10% top and bottom
    const triggerZoneHeight = window.innerHeight - rootMargin * 2;
    let targetDiagonal = isPortrait ? cardHeight : cardWidth;
    if (targetDiagonal > triggerZoneHeight) {
      targetDiagonal = triggerZoneHeight;
    }
    const scale = targetDiagonal / diagonal;
    const angle = -Math.atan(cardWidth / cardHeight) * (180 / Math.PI);
    if (isReturning) return 'rotateY(0) rotateZ(0)';
    if (isRotating) return `scale(${scale}) rotateY(1440deg) rotateZ(${angle}deg)`;
    if (isMoving) return `scale(${scale}) rotateY(0deg) rotateZ(${angle}deg)`;
    if (isBreathing) {
      if (breathingPhase === 'inhale' || breathingPhase === 'hold1') return 'scale(1)';
      if (breathingPhase === 'exhale' || breathingPhase === 'hold2') return 'scale(0.9)';
    }
    return 'none';
  }};

  &::before {
    opacity: ${({ isRotating }) => (isRotating ? 1 : 0)};
  }
`;
